import { useEffect, useId, useState } from 'react';
import { useTheme } from '@/lib/theme';
import { CodeView } from './code-view';

import type { RenderReply, RenderRequest } from '@/mermaid-frame';

/*
 * Mermaid diagrams are drawn in a hidden same-origin frame
 * (/mermaid-frame.html): mermaid sets inline style attributes while it
 * measures text, which this page's CSP forbids, so it runs in a page whose
 * own CSP allows them. The frame posts back SVG text, shown here through an
 * <img> data URL, so the SVG's styles also stay outside this page's CSP.
 */

let frame: Promise<HTMLIFrameElement> | null = null;
const pending = new Map<string, (reply: RenderReply) => void>();

/** The renderer frame, created on first use and kept for the page's lifetime. */
function rendererFrame(): Promise<HTMLIFrameElement> {
  return (frame ??= new Promise((resolve, reject) => {
    const el = document.createElement('iframe');
    el.src = '/mermaid-frame.html';
    el.title = 'Diagram renderer';
    el.tabIndex = -1;
    el.setAttribute('aria-hidden', 'true');
    // Off-screen but laid out (mermaid measures text), sized through classes: no inline styles.
    el.className = 'pointer-events-none fixed -left-[10000px] top-0 h-[800px] w-[1200px] border-0';
    const timeout = setTimeout(() => {
      frame = null;
      el.remove();
      reject(new Error('Diagram renderer did not load'));
    }, 20_000);
    window.addEventListener('message', (event: MessageEvent<RenderReply>) => {
      if (event.origin !== location.origin || event.source !== el.contentWindow) return;
      const reply = event.data;
      if (reply?.type === 'mermaid:ready') {
        clearTimeout(timeout);
        resolve(el);
      } else if (reply && 'id' in reply) {
        pending.get(reply.id)?.(reply);
        pending.delete(reply.id);
      }
    });
    document.body.appendChild(el);
  }));
}

/** Draws a diagram in the renderer frame and returns its SVG text. */
async function renderDiagram(id: string, source: string, theme: 'dark' | 'default') {
  const el = await rendererFrame();
  return new Promise<string>((resolve, reject) => {
    pending.set(id, (reply) =>
      reply.type === 'mermaid:result' ? resolve(reply.svg) : reject(new Error('Invalid diagram')),
    );
    const request: RenderRequest = { type: 'mermaid:render', id, source, theme };
    el.contentWindow!.postMessage(request, location.origin);
  });
}

/**
 * Gives the SVG a fixed pixel size (from its viewBox) so the <img> has an
 * intrinsic size. String edits only: parsing it with DOMParser would apply
 * the page's CSP to its styles and log violations.
 */
function sizeSvg(svg: string): { svg: string; width: number; height: number } | null {
  const open = /<svg\b[^>]*>/.exec(svg);
  if (!open) return null;
  const box =
    /\bviewBox="([^"]*)"/
      .exec(open[0])?.[1]
      ?.split(/[\s,]+/)
      .map(Number) ?? [];
  const width = Math.ceil(box[2] ?? 0);
  const height = Math.ceil(box[3] ?? 0);
  if (!width || !height) return null;
  const tag = open[0]
    .replace(/\s(?:width|height|style)="[^"]*"/g, '')
    .replace(/^<svg\b/, `<svg width="${width}" height="${height}"`);
  return {
    svg: svg.slice(0, open.index) + tag + svg.slice(open.index + open[0].length),
    width,
    height,
  };
}

/** A short text description for the image: the first line after the diagram type. */
function describe(source: string): string {
  const kind = source.trim().split(/\s/)[0] ?? 'diagram';
  const names: Record<string, string> = {
    flowchart: 'Flowchart',
    graph: 'Flowchart',
    sequenceDiagram: 'Sequence diagram',
    classDiagram: 'Class diagram',
    stateDiagram: 'State diagram',
    'stateDiagram-v2': 'State diagram',
    erDiagram: 'Entity relationship diagram',
    gantt: 'Gantt chart',
    pie: 'Pie chart',
    mindmap: 'Mind map',
    timeline: 'Timeline',
    journey: 'User journey',
  };
  return names[kind] ?? 'Diagram';
}

export function MermaidDiagram({ source }: { source: string }) {
  const { resolved } = useTheme();
  const rawId = useId();
  const id = `mmd-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; src: string; width: number; height: number }
    | { status: 'error' }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    // A new request id per render, so a reply for an older theme or source is ignored.
    renderDiagram(`${id}-${Date.now()}`, source, resolved === 'dark' ? 'dark' : 'default')
      .then((svg) => {
        if (cancelled) return;
        const sized = sizeSvg(svg);
        if (!sized) throw new Error('Empty diagram');
        setState({
          status: 'ready',
          src: `data:image/svg+xml;utf8,${encodeURIComponent(sized.svg)}`,
          width: sized.width,
          height: sized.height,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [source, resolved, id]);

  if (state.status === 'error') {
    return (
      <div className="md-diagram space-y-2">
        <p className="text-sm text-muted-foreground">This diagram couldn't be drawn. Its source:</p>
        <CodeView code={source} language="plaintext" />
      </div>
    );
  }
  if (state.status === 'loading') {
    return (
      <div
        className="md-diagram flex h-40 items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground"
        aria-busy
      >
        Drawing diagram…
      </div>
    );
  }
  return (
    <figure className="md-diagram overflow-x-auto">
      <img
        src={state.src}
        width={state.width}
        height={state.height}
        alt={describe(source)}
        className="mx-auto h-auto max-w-full rounded-none"
      />
      <figcaption className="sr-only">{source}</figcaption>
    </figure>
  );
}
